import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  IconButton,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import {
  formatNumber,
  parseNullableIntCell,
  fmtDateInput,
  fmtDateDisplay,
} from '@utils/usajiliFormat';
import {
  USAJILIDailyRecord,
  USAJILI_COUNTIES,
  USAJILI_NOT_CAPTURED,
  PaginatedResponse,
} from '@types';

/* ================================================================
   Inline edit helpers
================================================================= */
type SortDir = 'asc' | 'desc';
const SORTABLE_COLS = [
  'date',
  'county',
  'new_ids',
  'id_replacements',
  'voters_registered',
  'district',
  'division',
  'location',
] as const;
type SortCol = (typeof SORTABLE_COLS)[number] | '';

interface DailyTabProps {
  /** Set by the header's "Add row" button: open the add-row dialog on arrival. */
  openAdd?: boolean;
  onAddOpened?: () => void;
}

export const USAJILIDailyTab: React.FC<DailyTabProps> = ({ openAdd, onAddOpened }) => {
  const { filters, refetchKey, isAdmin, metadata, bumpRefetch } = useUSAJILI();
  const queryClient = useQueryClient();

  const [sortCol, setSortCol] = useState<SortCol>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [snack, setSnack] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const ordering = useMemo(() => {
    if (!sortCol) return 'date';
    return (sortDir === 'desc' ? '-' : '') + sortCol;
  }, [sortCol, sortDir]);

  const params = useMemo(
    () => ({
      ...filters,
      page,
      page_size: pageSize,
      ordering,
    }),
    [filters, page, pageSize, ordering]
  );

  const qKey = ['usajili_daily', params, refetchKey];
  const { data, isLoading, error } = useQuery<PaginatedResponse<USAJILIDailyRecord>>({
    queryKey: qKey,
    queryFn: () => usajiliApi.dailyList(params),
  });

  // ---- Inline edit state ---------------------------------------------------
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<Partial<USAJILIDailyRecord> | null>(null);

  const startEdit = (row: USAJILIDailyRecord) => {
    setEditingId(row.id);
    setEditingRow({ ...row });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingRow(null);
  };

  const patchField = <K extends keyof USAJILIDailyRecord>(k: K, v: USAJILIDailyRecord[K]) => {
    setEditingRow((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      usajiliApi.dailyUpdate(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['usajili_daily'] });
      bumpRefetch(); // force summary/charts/pivots to refresh too
      setSnack({ type: 'success', msg: 'Record updated' });
      cancelEdit();
    },
    onError: (e: any) => setSnack({ type: 'error', msg: e?.error?.message || 'Update failed' }),
  });

  const saveEdit = () => {
    if (!editingRow || editingId === null) return;
    const payload: Record<string, unknown> = {};
    payload.date = editingRow.date?.slice(0, 10);
    payload.county = editingRow.county;
    payload.new_ids =
      editingRow.new_ids === null
        ? null
        : editingRow.new_ids === undefined
        ? null
        : editingRow.new_ids;
    payload.id_replacements =
      editingRow.id_replacements === null ? null : editingRow.id_replacements;
    payload.voters_registered =
      editingRow.voters_registered === null ? null : editingRow.voters_registered;
    payload.district = editingRow.district || null;
    payload.division = editingRow.division || null;
    payload.location = editingRow.location || null;
    updateMut.mutate({ id: editingId, payload });
  };

  // ---- Delete --------------------------------------------------------------
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: number) => usajiliApi.dailyDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['usajili_daily'] });
      bumpRefetch();
      setSnack({ type: 'success', msg: 'Row deleted' });
      setDeleteId(null);
    },
    onError: (e: any) => setSnack({ type: 'error', msg: e?.error?.message || 'Delete failed' }),
  });

  // ---- Add row dialog ------------------------------------------------------
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    date: fmtDateInput(new Date()),
    county: USAJILI_COUNTIES[0],
    new_ids: '',
    id_replacements: '',
    voters_registered: '',
    district: '',
    division: '',
    location: '',
  });
  useEffect(() => {
    if (openAdd && isAdmin) {
      setAddOpen(true);
      onAddOpened?.();
    }
  }, [openAdd, isAdmin, onAddOpened]);

  const createMut = useMutation({
    mutationFn: (body: any) => usajiliApi.dailyCreate(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['usajili_daily'] });
      bumpRefetch();
      setSnack({ type: 'success', msg: 'Record created' });
      setAddOpen(false);
    },
    onError: (e: any) => setSnack({ type: 'error', msg: e?.error?.message || 'Create failed' }),
  });

  const onAddSubmit = () => {
    const payload: any = {
      date: addForm.date,
      county: addForm.county,
      new_ids: addForm.new_ids || '-',
      id_replacements: addForm.id_replacements || '-',
      voters_registered: addForm.voters_registered || '-',
    };
    if (addForm.district.trim()) payload.district = addForm.district.trim();
    if (addForm.division.trim()) payload.division = addForm.division.trim();
    if (addForm.location.trim()) payload.location = addForm.location.trim();
    createMut.mutate(payload);
  };

  // ---- Upload dialog trigger -------------------------------------------------
  // (actual upload dialog lives at the shell level for shared access; we just
  // provide a button that the shell intercepts via window dispatchEvent)
  const openUpload = () => {
    window.dispatchEvent(new CustomEvent('usajili:open-upload'));
  };
  const openAudit = () => {
    window.dispatchEvent(new CustomEvent('usajili:open-audit'));
  };

  // ---- Export (uses URLs from the API util) --------------------------------
  const doExport = (kind: 'daily' | 'summary') => {
    const url =
      kind === 'daily'
        ? usajiliApi.exportDailyCSV(filters)
        : usajiliApi.exportSummaryCSV(filters);
    window.open(url, '_blank');
  };

  const requestSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'date' ? 'desc' : 'asc');
    }
  };

  if (isLoading)
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {(error as any)?.error?.message || 'Failed to load Daily data'}
      </Alert>
    );

  const rows = data?.results || [];
  const total = data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const distValOr = (v: string | null | undefined) =>
    v ? v : USAJILI_NOT_CAPTURED;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Daily data{' '}
          <Chip size="small" label={`${total.toLocaleString()} rows`} color="default" sx={{ ml: 1 }} />
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <ButtonGroup size="small">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => doExport('daily')}
            >
              Export Daily
            </Button>
            <Button variant="outlined" onClick={() => doExport('summary')}>
              Export Summary
            </Button>
          </ButtonGroup>
          {isAdmin && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setAddOpen(true)}
              >
                Add row
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FileUploadIcon />}
                size="small"
                onClick={openUpload}
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={openAudit}
              >
                Audit log
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Paper
        sx={{
          width: '100%',
          overflow: 'auto',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 1100,
          }}
        >
          <TableHead>
            <TableRow>
              {(['date', 'county', 'new_ids', 'id_replacements', 'voters_registered', 'district', 'division', 'location'] as const).map(
                (c) => (
                  <TableCell
                    key={c}
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'background.default',
                      color: 'text.primary',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                    align={
                      c === 'new_ids' ||
                      c === 'id_replacements' ||
                      c === 'voters_registered'
                        ? 'right'
                        : 'inherit'
                    }
                    sortDirection={sortCol === c ? sortDir : undefined}
                  >
                    <TableSortLabel
                      active={sortCol === c}
                      direction={sortCol === c ? sortDir : 'asc'}
                      onClick={() => requestSort(c)}
                    >
                      {c.toUpperCase().replace(/_/g, ' ')}
                    </TableSortLabel>
                  </TableCell>
                )
              )}
              {isAdmin && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    minWidth: 120,
                  }}
                  align="center"
                >
                  ACTIONS
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  No rows match the current filters.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <TableRow
                  key={r.id}
                  hover
                  sx={{
                    '&:hover': { bgcolor: 'rgba(0,208,132,0.05)' },
                    ...(isEditing ? { bgcolor: 'rgba(255, 193, 7, 0.12)' } : {}),
                  }}
                >
                  {/* DATE */}
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        type="date"
                        size="small"
                        value={fmtDateInput((editingRow as any)?.date)}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => patchField('date', e.target.value)}
                      />
                    ) : (
                      fmtDateDisplay(r.date)
                    )}
                  </TableCell>
                  {/* COUNTY */}
                  <TableCell>
                    {isEditing ? (
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={(editingRow as any)?.county || ''}
                          onChange={(e) => patchField('county', e.target.value)}
                        >
                          {(metadata?.counties || USAJILI_COUNTIES).map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      r.county
                    )}
                  </TableCell>
                  {/* NEW IDS */}
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={
                          (editingRow as any)?.new_ids === null
                            ? '-'
                            : (editingRow as any)?.new_ids ?? ''
                        }
                        sx={{ width: 110 }}
                        inputProps={{ inputMode: 'numeric' }}
                        onChange={(e) => {
                          const parsed = parseNullableIntCell(e.target.value);
                          if (parsed && typeof parsed === 'object' && 'error' in parsed) {
                            return;
                          }
                          patchField('new_ids', parsed as any);
                        }}
                      />
                    ) : (
                      r.new_ids === null ? '-' : formatNumber(r.new_ids)
                    )}
                  </TableCell>
                  {/* ID REPLACEMENTS */}
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={
                          (editingRow as any)?.id_replacements === null
                            ? '-'
                            : (editingRow as any)?.id_replacements ?? ''
                        }
                        sx={{ width: 110 }}
                        inputProps={{ inputMode: 'numeric' }}
                        onChange={(e) => {
                          const parsed = parseNullableIntCell(e.target.value);
                          if (parsed && typeof parsed === 'object' && 'error' in parsed) return;
                          patchField('id_replacements', parsed as any);
                        }}
                      />
                    ) : (
                      r.id_replacements === null ? '-' : formatNumber(r.id_replacements)
                    )}
                  </TableCell>
                  {/* VOTERS REGISTERED */}
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={
                          (editingRow as any)?.voters_registered === null
                            ? '-'
                            : (editingRow as any)?.voters_registered ?? ''
                        }
                        sx={{ width: 110 }}
                        inputProps={{ inputMode: 'numeric' }}
                        onChange={(e) => {
                          const parsed = parseNullableIntCell(e.target.value);
                          if (parsed && typeof parsed === 'object' && 'error' in parsed) return;
                          patchField('voters_registered', parsed as any);
                        }}
                      />
                    ) : (
                      r.voters_registered === null ? '-' : formatNumber(r.voters_registered)
                    )}
                  </TableCell>
                  {/* DISTRICT */}
                  <TableCell>
                    {isEditing ? (() => {
                      const editCounty = (editingRow as any)?.county || '';
                      const distOpts = metadata?.districts_by_county?.[editCounty] || [];
                      return distOpts.length ? (
                        <Select
                          size="small"
                          value={(editingRow as any)?.district || ''}
                          onChange={(e) => { patchField('district', e.target.value); patchField('division', null as any); patchField('location', null as any); }}
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="">— None —</MenuItem>
                          {distOpts.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                      ) : (
                        <TextField size="small" value={(editingRow as any)?.district || ''} onChange={(e) => patchField('district', e.target.value)} sx={{ width: 130 }} />
                      );
                    })() : distValOr(r.district)}
                  </TableCell>
                  {/* DIVISION */}
                  <TableCell>
                    {isEditing ? (() => {
                      const editCounty = (editingRow as any)?.county || '';
                      const editDistrict = (editingRow as any)?.district || '';
                      const divOpts = metadata?.divisions_by_key?.[`${editCounty}|${editDistrict}`] || [];
                      return divOpts.length ? (
                        <Select
                          size="small"
                          value={(editingRow as any)?.division || ''}
                          onChange={(e) => { patchField('division', e.target.value); patchField('location', null as any); }}
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="">— None —</MenuItem>
                          {divOpts.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                      ) : (
                        <TextField size="small" value={(editingRow as any)?.division || ''} onChange={(e) => patchField('division', e.target.value)} sx={{ width: 130 }} />
                      );
                    })() : distValOr(r.division)}
                  </TableCell>
                  {/* LOCATION */}
                  <TableCell>
                    {isEditing ? (() => {
                      const editCounty = (editingRow as any)?.county || '';
                      const editDistrict = (editingRow as any)?.district || '';
                      const editDivision = (editingRow as any)?.division || '';
                      const locOpts = metadata?.locations_by_key?.[`${editCounty}|${editDistrict}|${editDivision}`] || [];
                      return locOpts.length ? (
                        <Select
                          size="small"
                          value={(editingRow as any)?.location || ''}
                          onChange={(e) => patchField('location', e.target.value)}
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="">— None —</MenuItem>
                          {locOpts.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                      ) : (
                        <TextField size="small" value={(editingRow as any)?.location || ''} onChange={(e) => patchField('location', e.target.value)} sx={{ width: 130 }} />
                      );
                    })() : distValOr(r.location)}
                  </TableCell>
                  {/* ACTIONS */}
                  {isAdmin && (
                    <TableCell align="center">
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={saveEdit}
                              disabled={updateMut.isPending}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="default" onClick={cancelEdit}>
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => startEdit(r)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(r.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mt: 2,
        }}
      >
        <Box>
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel id="usajili-daily-ps">Rows per page</InputLabel>
            <Select
              labelId="usajili-daily-ps"
              label="Rows per page"
              value={String(pageSize)}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[25, 50, 100, 250, 500].map((n) => (
                <MenuItem key={n} value={String(n)}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span style={{ color: '#8b95a5' }}>
            Showing <b>{Math.min((page - 1) * pageSize + 1, total)}</b> to{' '}
            <b>{Math.min(page * pageSize, total)}</b> of <b>{total.toLocaleString()}</b>
          </span>
        </Box>
        <ButtonGroup size="small">
          <Button onClick={() => setPage(1)} disabled={page === 1}>
            First
          </Button>
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Prev
          </Button>
          <Button disabled>
            Page {page} / {totalPages}
          </Button>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
          <Button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>
            Last
          </Button>
        </ButtonGroup>
      </Box>

      {/* Delete confirm dialog */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete this row?</DialogTitle>
        <DialogContent sx={{ minWidth: { sm: 420 } }}>
          This will permanently remove the selected Daily data record. The Summary
          Table, charts and pivot tabs will recalculate automatically.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteId !== null && deleteMut.mutate(deleteId)}
            disabled={deleteMut.isPending}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add row dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a Daily data row</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={addForm.date}
              onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel id="add-county-lbl">County</InputLabel>
              <Select
                labelId="add-county-lbl"
                label="County"
                value={addForm.county}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, county: e.target.value, district: '', division: '', location: '' }))
                }
              >
                {(metadata?.counties || USAJILI_COUNTIES).map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sub-county cascade */}
            <FormControl fullWidth disabled={!addForm.county || !(metadata?.districts_by_county?.[addForm.county]?.length)}>
              <InputLabel id="add-district-lbl">Sub-county / District</InputLabel>
              <Select
                labelId="add-district-lbl"
                label="Sub-county / District"
                value={addForm.district}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, district: e.target.value, division: '', location: '' }))
                }
              >
                <MenuItem value="">— None —</MenuItem>
                {(metadata?.districts_by_county?.[addForm.county] || []).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!addForm.district || !(metadata?.divisions_by_key?.[`${addForm.county}|${addForm.district}`]?.length)}>
              <InputLabel id="add-division-lbl">Division</InputLabel>
              <Select
                labelId="add-division-lbl"
                label="Division"
                value={addForm.division}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, division: e.target.value, location: '' }))
                }
              >
                <MenuItem value="">— None —</MenuItem>
                {(metadata?.divisions_by_key?.[`${addForm.county}|${addForm.district}`] || []).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!addForm.division || !(metadata?.locations_by_key?.[`${addForm.county}|${addForm.district}|${addForm.division}`]?.length)}>
              <InputLabel id="add-location-lbl">Location</InputLabel>
              <Select
                labelId="add-location-lbl"
                label="Location"
                value={addForm.location}
                onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))}
              >
                <MenuItem value="">— None —</MenuItem>
                {(metadata?.locations_by_key?.[`${addForm.county}|${addForm.district}|${addForm.division}`] || []).map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="New IDs"
                helperText={`Number >= 0 or "-"`}
                value={addForm.new_ids}
                onChange={(e) => setAddForm((f) => ({ ...f, new_ids: e.target.value }))}
              />
              <TextField
                label="ID Replacements"
                helperText=">= 0 or '-'"
                value={addForm.id_replacements}
                onChange={(e) => setAddForm((f) => ({ ...f, id_replacements: e.target.value }))}
              />
              <TextField
                label="Voters Registered"
                helperText=">= 0 or '-'"
                value={addForm.voters_registered}
                onChange={(e) => setAddForm((f) => ({ ...f, voters_registered: e.target.value }))}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onAddSubmit}
            disabled={createMut.isPending || !addForm.date || !addForm.county}
          >
            {createMut.isPending ? 'Saving…' : 'Add row'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack !== null}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack?.type || 'success'}
          onClose={() => setSnack(null)}
          sx={{ width: '100%' }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
